import React from 'react';
import { Kiwify as Icon, Copy } from '@profitfy/pakkun-icons';
import { yupResolver } from '@hookform/resolvers/yup';
import { useParams, useHistory } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useTheme } from 'styled-components/macro';

import { EHeadingWeight } from '@domain/enums/components/EHeading';
import { ETextSize, ETextWeight } from '@domain/enums/components/EText';
import { IParams } from '@domain/interfaces/IParams';
import { EKiwifyType } from '@domain/enums/common/integrations/providers/EKiwify';

import { useToast } from '@helpers/hooks/useToast';
import { useInfoProduct } from '@helpers/hooks/pages/onboarding/useInfoProduct';
import { useOnboarding } from '@helpers/hooks/pages/onboarding/useOnboarding';

import { getTranslatedIntegrationsError } from '@helpers/utils/errors/integrations/integrations';

import { kiwifySchema } from '@helpers/validators/dashboard/integrationCenter/infoProduct/kiwify';

import kiwifyService from '@services/pages/dashboard/integrationCenter/infoProduct/platforms/kiwifyAuth';

import NextStep from '@components/Onboarding/NextStep';
import Text from '@components/common/core/DataDisplay/Text';

import SkeletonLoading from './SkeletonLoading';

import * as S from './styles';

const Kiwify: React.FC = () => {
  const theme = useTheme();
  const history = useHistory();
  const { toast } = useToast();
  const { storeAliasId } = useParams<IParams>();
  const { selectedMarketings } = useInfoProduct();
  const { isLoadingTrial, handleStoreBonus } = useOnboarding();
  const {
    handleSubmit,
    register,
    formState: { errors },
  } = useForm({ mode: 'onBlur', resolver: yupResolver(kiwifySchema) });

  const [webhookURL, setWebhookURL] = React.useState<string>('');
  const [accessTokenValue, setAccessTokenValue] = React.useState<string>('');
  const [secretKey, setSecretKey] = React.useState<string>('');
  const [isURLCopied, setIsURLCopied] = React.useState<boolean>(false);
  const [isGettingWebhookURL, setIsGettingWebhookURL] = React.useState<boolean>(false);
  const [isCreatingCredential, setIsCreatingCredential] = React.useState<boolean>(false);

  const accessTokenInputRegister = register('access_token');

  const onAccessTokenChange = React.useCallback(
    event => {
      accessTokenInputRegister.onChange(event);

      setAccessTokenValue(event.target.value);
    },
    [accessTokenInputRegister],
  );

  const handleCopyURL = React.useCallback(() => {
    navigator.clipboard.writeText(webhookURL);
    setIsURLCopied(true);
    toast.success('URL copiada com sucesso.');
  }, [toast, webhookURL]);

  const onSubmit = React.useCallback(
    async formData => {
      setIsCreatingCredential(true);

      const data = {
        webhook_token: formData.access_token,
        secret_key: secretKey,
        types: [EKiwifyType.STORE],
      };

      try {
        await kiwifyService.createCredential({ storeAliasId, data });

        toast.success('Credencial criada com sucesso.');

        setIsCreatingCredential(false);

        if (selectedMarketings.length) {
          history.push(`/onboarding/${storeAliasId}/info-product/integrations/marketing`);
        } else {
          await handleStoreBonus();
        }
      } catch (error: any) {
        toast.error(getTranslatedIntegrationsError(error));
      } finally {
        setIsCreatingCredential(false);
      }
    },
    [secretKey, storeAliasId, toast, selectedMarketings, history, handleStoreBonus],
  );

  const generateWebhookURL = React.useCallback(async () => {
    setIsGettingWebhookURL(true);

    try {
      const { data } = await kiwifyService.generateWebhookURL({ storeAliasId });

      setWebhookURL(data?.webhook_url);
      setSecretKey(data?.secret_key);
      setIsGettingWebhookURL(false);
    } catch (error: any) {
      setIsGettingWebhookURL(false);
      toast.error(error?.response?.data?.message);
    }
  }, [toast, storeAliasId]);

  React.useEffect(() => {
    generateWebhookURL();
  }, [generateWebhookURL]);

  if (isGettingWebhookURL) {
    return (
      <>
        <SkeletonLoading />
        <NextStep>Próximo passo</NextStep>
      </>
    );
  }

  return (
    <S.Wrapper>
      <S.FormWrapper>
        <S.Form onSubmit={handleSubmit(onSubmit)}>
          <S.Header>
            <S.Title>Integre sua plataforma de infoproduto</S.Title>
          </S.Header>

          <S.IntegrationTitle>
            <Icon size={36} />

            <S.IntegrationTitleText fontWeight={EHeadingWeight.REGULAR}>
              Integração com Kiwify
            </S.IntegrationTitleText>
          </S.IntegrationTitle>

          <S.Body>
            <S.InputGroup>
              <S.Label>URL do webhook</S.Label>

              <S.WebhookURLWrapper onClick={handleCopyURL} type="button">
                <S.WebhookURL weight={ETextWeight.MEDIUM}>{webhookURL}</S.WebhookURL>

                <S.IconWrapper>
                  <Copy size={18} color={theme.colors.gray[1]} outline={!isURLCopied} />
                </S.IconWrapper>
              </S.WebhookURLWrapper>
            </S.InputGroup>

            <S.InputGroup>
              <S.Label>Token</S.Label>

              <S.Input
                {...accessTokenInputRegister}
                type="text"
                value={accessTokenValue}
                placeholder="Kiwify Token"
                onChange={onAccessTokenChange}
                isError={errors?.access_token}
              />
              {errors.access_token && <Text isErrorFeedback>{errors.access_token.message}</Text>}
            </S.InputGroup>
          </S.Body>

          <NextStep
            type="submit"
            isLoading={isCreatingCredential || isLoadingTrial}
            disabled={isCreatingCredential || !accessTokenValue || isLoadingTrial}
          >
            Próximo passo
          </NextStep>
        </S.Form>
      </S.FormWrapper>

      <S.TutorialWrapper>
        <S.TutorialImage
          src="https://s3.us-west-2.amazonaws.com/app.profitfy.me-media/onboarding/images/content/onboarding/onboarding-integration-tutorial.svg"
          alt="Profitfy.me Integration Tutorial"
        />
        <S.TutotialTitle weight={ETextWeight.BOLD}>Tutorial de integração</S.TutotialTitle>

        <S.TutotialStep weight={ETextWeight.BOLD} size={ETextSize.B5}>
          1 - Copie a URL no campo &quot;URL do webhook&quot;.
        </S.TutotialStep>

        <S.TutotialStep weight={ETextWeight.BOLD} size={ETextSize.B5}>
          2 -{' '}
          <S.Link href="https://dashboard.kiwify.com.br/apps/webhooks/integrations" target="_blank">
            Clique aqui e vá para Kiwify.
          </S.Link>
        </S.TutotialStep>

        <S.StepsWrapper>
          <S.StepWrapper>
            <S.StepBullet />
            <S.StepText size={ETextSize.B5}>Clique em &quot;Criar webhook&quot;</S.StepText>
          </S.StepWrapper>
          <S.StepWrapper>
            <S.StepBullet />
            <S.StepText size={ETextSize.B5}>Cole no campo &quot;URL do Webhook&quot;</S.StepText>
          </S.StepWrapper>
        </S.StepsWrapper>

        <S.TutotialStep weight={ETextWeight.BOLD} size={ETextSize.B5}>
          3 - Após colar, copie o token do campo “Token” na Kiwify e cole no campo “Token” da
          Profitfy.
        </S.TutotialStep>
      </S.TutorialWrapper>
    </S.Wrapper>
  );
};

export default Kiwify;
